import { useNavigate } from "react-router-dom";
import { ScoreRing, PriorityBadge } from "@/components/Score";
import { Button } from "@/components/ui/button";
import { moneyRange } from "@/lib/format";
import { MapPin, Star, TrendingUp, Ban, ArrowRight, BadgeCheck } from "lucide-react";

export function OpportunityCard({ opp, rank, currency = "INR", onGeneratePitch }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={`opportunity-card-${opp.id}`}
      className="lf-card p-5 flex flex-col gap-4 hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {rank && (
              <span className="text-xs font-mono font-bold text-primary">#{rank}</span>
            )}
            <PriorityBadge label={opp.priority_label} testid={`priority-${opp.id}`} />
            {opp.confidence_label === "Low" && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border text-amber-400 bg-amber-500/10 border-amber-500/25" title="Limited public data — score & value are low-confidence estimates" data-testid={`confidence-low-${opp.id}`}>LOW CONFIDENCE</span>
            )}
          </div>
          <h3 className="font-display font-bold text-lg text-slate-50 truncate">{opp.name}</h3>
          <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.area}, {opp.city}</span>
            {opp.rating != null ? (
              <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{opp.rating} · {opp.reviews}{((opp.verified_fields || []).includes("rating") || opp.provider === "google") && <BadgeCheck className="w-3 h-3 text-emerald-400" title="Verified · Google Places" />}</span>
            ) : (
              <span className="text-slate-500 font-mono text-[11px]" title="Ratings not published by this data source — Not verified">Ratings N/V</span>
            )}
          </div>
        </div>
        <ScoreRing score={opp.opportunity_score} size={72} label="Score" />
      </div>

      {opp.priority_label === "LOW PRIORITY" ? (
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/40 px-3 py-2.5">
          <p className="text-xs font-semibold text-slate-300">Not a strong prospect right now.</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{(opp.low_reasons || []).join(" · ")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-lg bg-primary/5 border border-primary/15 px-3 py-2.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-primary mb-0.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Recommended
            </p>
            <p className="text-sm font-semibold text-slate-100">{opp.primary_service}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-slate-500 mb-0.5">Est. opportunity</p>
              <p className="font-semibold text-emerald-400 lf-tabnum">{moneyRange(opp.estimated_value_min, opp.estimated_value_max, opp.currency || currency)}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-0.5 flex items-center gap-1"><Ban className="w-3 h-3" /> Don't pitch</p>
              <p className="font-semibold text-slate-300">{opp.do_not_pitch}</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 italic border-l-2 border-primary/40 pl-2.5">"{opp.sales_angle}"</p>
        </>
      )}

      <div className="flex gap-2 mt-auto pt-1">
        <Button
          variant="outline" size="sm" className="flex-1 border-slate-700 hover:bg-slate-800 text-slate-200"
          data-testid={`view-opportunity-${opp.id}`}
          onClick={() => navigate(`/app/opportunity/${opp.id}`)}
        >
          View opportunity <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
        {opp.priority_label !== "LOW PRIORITY" && onGeneratePitch && (
          <Button
            size="sm" className="bg-primary hover:bg-emerald-600 text-white"
            data-testid={`generate-pitch-${opp.id}`}
            onClick={() => onGeneratePitch(opp)}
          >
            Generate pitch
          </Button>
        )}
      </div>
    </div>
  );
}
